import { UsersAdminUtils, type UsersAdminUser } from "./users-admin-utils";
import { FormBase } from "../../util/form-base-form";

export class UsersAdminForm extends FormBase {
    public root: HTMLElement;

    public roles: typeof UsersAdminUtils.ROLES;

    public constructor(root: HTMLElement) {
        super();
        this.root = root;
        this.roles = UsersAdminUtils.ROLES;
    }

    public static getRoots(root: ParentNode): HTMLElement[] {
        return new UsersAdminForm(document.body).queryElements(
            root,
            `[data-role="${UsersAdminUtils.ROLES.ROOT}"]`,
            HTMLElement,
        );
    }

    public getRoot(): HTMLElement {
        return this.root;
    }

    public isInitialized(): boolean {
        return this.root.dataset.usersAdminInit === "1";
    }

    public markInitialized(): void {
        this.root.dataset.usersAdminInit = "1";
    }

    public getStatusEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.STATUS}"]`, HTMLElement);
    }

    public getCountEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.COUNT}"]`, HTMLElement);
    }

    public getSearchEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.SEARCH}"]`, HTMLElement);
    }

    public getCreateButton(root: ParentNode = document): HTMLButtonElement | null {
        return this.queryElement(root, '[data-role="master-add-open"]', HTMLButtonElement);
    }

    public getRoleFilterEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.ROLE_FILTER}"]`, HTMLElement);
    }

    public getUserStatusFilterEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.STATUS_FILTER}"]`, HTMLElement);
    }

    public getIncludeDummyFilterEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.INCLUDE_DUMMY_FILTER}"]`, HTMLElement);
    }

    public getListEl(): Element | null {
        return this.queryElement(this.root, `[data-role="${this.roles.LIST}"]`, HTMLElement);
    }

    public getEditButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(root, `[data-role="${this.roles.EDIT}"]`, HTMLButtonElement);
    }

    public getHistoryButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(root, `[data-role="${this.roles.HISTORY}"]`, HTMLButtonElement);
    }

    public getSaveButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(root, `[data-role="${this.roles.SAVE}"]`, HTMLButtonElement);
    }

    public getDeleteButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null {
        return this.closest(root, `[data-role="${this.roles.DELETE}"]`, HTMLButtonElement);
    }

    public getRow(root: EventTarget | Element | null | undefined): HTMLElement | null {
        return this.closest(root, ".users-admin__row", HTMLElement);
    }

    public getRoleSelect(root: ParentNode): HTMLSelectElement | null {
        return this.queryElement(root, '[data-field="role"]', HTMLSelectElement);
    }

    public getUserStatusSelect(root: ParentNode): HTMLSelectElement | null {
        return this.queryElement(root, '[data-field="user_status"]', HTMLSelectElement);
    }

    public getUserNoteInput(root: ParentNode): HTMLTextAreaElement | null {
        return this.queryElement(root, '[data-field="user_note"]', HTMLTextAreaElement);
    }

    public getHistoryModal(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.HISTORY_MODAL}"]`, HTMLElement);
    }

    public getHistoryOverlay(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.HISTORY_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getHistoryCloseButtons(): HTMLElement[] {
        return this.queryElements(this.root, `[data-role="${this.roles.HISTORY_MODAL_CLOSE}"]`, HTMLElement);
    }

    public getHistoryTitleEl(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.HISTORY_MODAL_TITLE}"]`, HTMLElement);
    }

    public getHistoryStatusEl(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.HISTORY_MODAL_STATUS}"]`, HTMLElement);
    }

    public getHistorySpinnerEl(): HTMLElement | null {
        const modal = this.getHistoryModal();
        if (!(modal instanceof HTMLElement)) {
            return null;
        }

        return this.queryElement(modal, `[data-role="${this.roles.HISTORY_MODAL_SPINNER}"]`, HTMLElement);
    }

    public getHistoryBodyEl(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.HISTORY_MODAL_BODY}"]`, HTMLElement);
    }

    public getCreateModal(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_MODAL}"]`, HTMLElement);
    }

    public getCreateOverlay(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_MODAL_OVERLAY}"]`, HTMLElement);
    }

    public getCreateCloseButtons(): HTMLElement[] {
        return this.queryElements(this.root, `[data-role="${this.roles.CREATE_MODAL_CLOSE}"]`, HTMLElement);
    }

    public getCreateForm(): HTMLFormElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_FORM}"]`, HTMLFormElement);
    }

    public getCreateEmailInput(): HTMLInputElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_EMAIL}"]`, HTMLInputElement);
    }

    public getCreateNicknameInput(): HTMLInputElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_NICKNAME}"]`, HTMLInputElement);
    }

    public getCreateSubmitButton(): HTMLButtonElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_SUBMIT}"]`, HTMLButtonElement);
    }

    public getCreateResultEl(): HTMLElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_RESULT}"]`, HTMLElement);
    }

    public getCreateResultPasswordInput(): HTMLInputElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_RESULT_PASSWORD}"]`, HTMLInputElement);
    }

    public getCreateResultEmailInput(): HTMLInputElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_RESULT_EMAIL}"]`, HTMLInputElement);
    }

    public getCreateResultNicknameInput(): HTMLInputElement | null {
        return this.queryElement(this.root, `[data-role="${this.roles.CREATE_RESULT_NICKNAME}"]`, HTMLInputElement);
    }

    public getUsersData(): UsersAdminUser[] {
        const scriptEl = this.queryElement(this.root, `script[data-role="${this.roles.DATA}"]`, HTMLScriptElement);
        if (!(scriptEl instanceof HTMLScriptElement)) {
            return [];
        }

        try {
            return UsersAdminUtils.toUsersArray(JSON.parse(scriptEl.textContent || "[]"));
        } catch {
            return [];
        }
    }
}
